import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import { FaFileExcel, FaFilter, FaPlus } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import { motion } from "framer-motion";
import { SuwasthiLoader } from "../components/loader";
import Pagination from "../components/pagination"; // ✅ Your reusable pagination
import { useUser } from "../config/userProvider";
import { hasPermission } from "../config/permissions";

const BatchListing = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  const navigate = useNavigate();
  let { userData } = useUser();

  useEffect(() => {
    fetchFilterBatches();
  }, [currentPage, itemsPerPage, search, startDate, endDate, filterStatus]);

  const fetchFilterBatches = () => {
    setLoading(true);

    const params = new URLSearchParams();
    params.append("page", currentPage);
    params.append("limit", itemsPerPage);
    if (startDate) params.append("startDate", new Date(startDate).toLocaleDateString("en-CA"));
    if (endDate) params.append("endDate", new Date(endDate).toLocaleDateString("en-CA"));
    if (filterStatus) params.append("status", filterStatus);
    if (search) params.append("search", search);

    fetch(`${apiurl}/api/batch/get?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setBatches(data.batches || []);
        setTotalItems(data.pagination?.totalItems || 0);
        setTotalPages(data.pagination?.totalPages || 1);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching batches:", err);
        setLoading(false);
      });
  };

  const exportToExcel = () => {
    if (!batches.length) {
      alert("No batch data found for export.");
      return;
    }

    const formattedData = batches.map((batch, index) => ({
      SNO: index + 1,
      ID: batch.id,
      NAME: batch.batchName,
      DESCRIPTION: batch.description || "",
      STATUS: batch.batchStatus,
      "APPLICATION COUNT": batch.applications?.length || 0,
      "CREATED BY": batch.createdBy?.userName || "",
      "CREATED AT": new Date(batch.createdAt).toLocaleDateString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BATCHES");

    const fileName = `BATCHES_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const listVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: { delay: index * 0.1, duration: 0.4 },
    }),
  };

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="p-2 bg-gray-100 ">
          {/* 🔍 Search + Actions */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
            {/* Search */}
            <div className="flex items-center gap-2 w-full md:w-1/3">
              <input
                type="text"
                placeholder="Search by name, id, or description"
                className="p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 justify-start md:justify-end items-center w-full md:w-auto">
              <select
                className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1); // reset to first page
                }}
              >
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>

              <button
                onClick={() => setFilterModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-blue-500 hover:bg-blue-600 transition"
              >
                <FaFilter /> Filter
              </button>

              <Link
                to="/batches/create"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-indigo-500 hover:bg-indigo-600 transition"
              >
                <FaPlus /> Add Batch
              </Link>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <SuwasthiLoader />
          ) : (
            <>
              <div className="bg-white shadow-md rounded-lg p-4 overflow-x-auto">
                <table className="w-full border-collapse text-[14px]">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="p-3 text-left">Batch ID</th>
                      <th className="p-3 text-left">Batch Name</th>
                      <th className="p-3 text-left">Applications</th>
                      <th className="p-3 text-left">Status</th>
                      <th className="p-3 text-left">Created By</th>
                      <th className="p-3 text-left">Created At</th>
                      <th className="p-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((batch, index) => (
                      <motion.tr
                        key={batch._id}
                        className="border-b text-[12px] hover:bg-gray-50"
                        variants={listVariants}
                        initial="hidden"
                        animate="visible"
                        custom={index}
                      >
                        <td className="p-3">{batch.id}</td>
                        <td className="p-3">{batch.batchName}</td>
                        <td className="p-3">
                        <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs mr-1">
                         Total: {batch.applications?.length || 0}
                        </span>


                        {/* <td className="p-3">
  <div className="flex flex-col gap-1">
    <span className="font-semibold">
      Total: {batch.batchesReport?.totalApplications || 0}
    </span>

    {batch.batchesReport?.statusReport &&
      Object.entries(batch.batchesReport.statusReport).map(([status, count]) => (
        <span key={status} className="text-sm text-gray-600">
          {status}: {count}
        </span>
      ))}
  </div>
</td> */}


{/* {Object.entries(batch.batchesReport?.statusReport || {}).map(([status, count]) => (
  <span
    key={status}
    className="inline-block px-2 py-1 mr-1 rounded-full text-xs bg-gray-200 text-gray-800"
  >
    {status}: {count}
  </span>
))} */}


{/* {(() => {
  const statusColors = {
    total: "bg-blue-500 text-white",
    hired: "bg-green-500 text-white",
    training: "bg-yellow-400 text-black",
    joined: "bg-violet-500 text-white",
    absconded: "bg-red-500 text-white",
  };

  const order = ["total", "hired", "training", "joined", "absconded"];

  return order.map((status) => {
    const count =
      status === "total"
        ? batch.batchesReport?.totalApplications || 0
        : batch.batchesReport?.statusReport?.[status] || 0;

    return (
      <span
        key={status}
        className={`inline-block px-2 py-1 mr-1 rounded-full text-xs ${
          statusColors[status] || "bg-gray-200 text-gray-800"
        }`}
      >
        {status}: {count}
      </span>
    );
  });
})()} */}

{Object.entries(batch.batchesReport?.statusReport || {}).map(([status, count]) => {
  const statusColors = {
    hired: "bg-green-500 text-white",
    absconded: "bg-red-500 text-white",
    training: "bg-yellow-400 text-black",
    joined: "bg-violet-500 text-white",
    total: "bg-blue-500 text-white",
  };

  const badgeClass =
    statusColors[status.toLowerCase()] || "bg-gray-200 text-gray-800";

  return (
    <>

    <span
      key={status}
      className={`inline-block px-2 py-1 mr-1 rounded-full text-xs ${badgeClass}`}
    >
      {status}: {count}
    </span>
    </>
  );
})}
</td>



                        <td className="p-3">{batch.batchStatus}</td>
                        <td className="p-3">{batch.createdBy?.userName}</td>
                        <td className="p-3">
                          {new Date(batch.createdAt).toLocaleDateString()}
                        </td>
                        <td className="p-3 flex gap-2">
                          {hasPermission(userData.permissions, "batch", "read") && (
                            <button
                              className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                              onClick={() => navigate(`/batches/${batch._id}`)}
                            >
                              View
                            </button>
                          )}
                          {hasPermission(userData.permissions, "batch", "write") && (
                            <Link
                              className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                              to={`/batches/${batch._id}/manage`}
                            >
                              Manage
                            </Link>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ✅ Pagination Section */}
              <div className="mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            </>
          )}
        </div>

        {/* Filter Modal */}
        {filterModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4">Filter Batches</h2>

              <div className="mb-4">
                <label className="block text-sm mb-1">Start Date:</label>
                <DatePicker
                  selected={startDate}
                  onChange={setStartDate}
                  placeholderText="Start Date"
                  className="p-2 border rounded w-full"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm mb-1">End Date:</label>
                <DatePicker
                  selected={endDate}
                  onChange={setEndDate}
                  placeholderText="End Date"
                  className="p-2 border rounded w-full"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm mb-1">Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="p-2 border rounded w-full"
                >
                  <option value="">All</option>
                  <option value="induction">Induction</option>
                  <option value="training">Training</option>
                  <option value="handoverToTrainer">Handover to Trainer</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  className="bg-gray-300 text-black px-4 py-2 rounded"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setFilterStatus("");
                    setFilterModalOpen(false);
                  }}
                >
                  Reset
                </button>
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                  onClick={() => setFilterModalOpen(false)}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default BatchListing;